// crewbit-admin/src/components/money/OutstandingTable.tsx
//
// Admin Outstanding table. Groups rows by aging bucket (most severe first)
// with bucket section headers, then renders each row via OutstandingTableRow.
//
// SPEC: docs/references/MONEY_MODULE_SPEC_v1.md §Outstanding.7.2.
// Default sort: days_outstanding descending (most overdue first), stable —
// preserves due_date ascending order from the hook within same-day groups.
// Sortable column headers deferred to future polish.

import { useMemo } from 'react';
import { palette, urgency } from '../../styles/tokens';
import { bucketTone, type AgingBucket } from '../../lib/aging';
import { formatMoney } from '../../lib/money';
import {
  OutstandingTableRow as OutstandingTableRowComponent,
} from './OutstandingTableRow';
import type { OutstandingInvoiceRow } from '../../hooks/useOutstandingInvoices';

interface OutstandingTableProps {
  rows: OutstandingInvoiceRow[];
  onRowTap: (row: OutstandingInvoiceRow) => void;
  onRowRemind: (row: OutstandingInvoiceRow) => void;
}

const thStyle: React.CSSProperties = {
  padding: '12px 14px',
  fontSize: 11.5,
  fontWeight: 600,
  color: palette.g500,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  textAlign: 'left',
  borderBottom: `1px solid ${palette.g150}`,
  background: palette.g50,
};

const BUCKET_ORDER: AgingBucket[] = ['61_plus', '31_60', '1_30', 'current'];

const BUCKET_LABEL: Record<AgingBucket, string> = {
  current: 'Current',
  '1_30': '1–30 days',
  '31_60': '31–60 days',
  '61_plus': '61+ days',
};

interface ToneTreatment {
  bg: string;
  fg: string;
}

function toneHeaderTreatment(tone: 'neutral' | 'warning' | 'danger'): ToneTreatment {
  switch (tone) {
    case 'neutral': return { bg: palette.g100, fg: palette.g600 };
    case 'warning': return { bg: palette.amberWash, fg: urgency.warning };
    case 'danger':  return { bg: palette.redWash,   fg: palette.red };
  }
}

interface BucketGroup {
  bucket: AgingBucket;
  rows: OutstandingInvoiceRow[];
  total: number;
}

function groupByBucket(rows: OutstandingInvoiceRow[]): BucketGroup[] {
  const buckets = new Map<AgingBucket, OutstandingInvoiceRow[]>();
  for (const r of rows) {
    const list = buckets.get(r.bucket);
    if (list) list.push(r);
    else buckets.set(r.bucket, [r]);
  }
  return BUCKET_ORDER
    .filter((b) => buckets.has(b))
    .map((b) => {
      const groupRows = buckets.get(b)!;
      const total = groupRows.reduce((sum, r) => sum + r.total, 0);
      return { bucket: b, rows: groupRows, total };
    });
}

export function OutstandingTable({ rows, onRowTap, onRowRemind }: OutstandingTableProps) {
  const groups = useMemo(() => {
    const sorted = [...rows].sort((a, b) => b.days_outstanding - a.days_outstanding);
    return groupByBucket(sorted);
  }, [rows]);

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={thStyle}>Invoice</th>
          <th style={thStyle}>Client</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Days Out</th>
          <th style={thStyle}>Last Reminded</th>
          <th style={thStyle}>Status</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Action</th>
        </tr>
      </thead>
      <tbody>
        {groups.map((group) => {
          const treatment = toneHeaderTreatment(bucketTone(group.bucket));
          const countLabel = `${group.rows.length} invoice${group.rows.length === 1 ? '' : 's'}`;
          return (
            <BucketSection
              key={group.bucket}
              group={group}
              countLabel={countLabel}
              treatment={treatment}
              onRowTap={onRowTap}
              onRowRemind={onRowRemind}
            />
          );
        })}
      </tbody>
    </table>
  );
}

interface BucketSectionProps {
  group: BucketGroup;
  countLabel: string;
  treatment: ToneTreatment;
  onRowTap: (row: OutstandingInvoiceRow) => void;
  onRowRemind: (row: OutstandingInvoiceRow) => void;
}

function BucketSection({ group, countLabel, treatment, onRowTap, onRowRemind }: BucketSectionProps) {
  return (
    <>
      <tr aria-label={`${BUCKET_LABEL[group.bucket]} bucket header`}>
        <td
          colSpan={7}
          style={{
            padding: '10px 14px',
            background: treatment.bg,
            borderBottom: `1px solid ${palette.g150}`,
            borderTop: `1px solid ${palette.g150}`,
            fontSize: 11.5,
            fontWeight: 600,
            color: treatment.fg,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            cursor: 'default',
          }}
        >
          <span>{BUCKET_LABEL[group.bucket]}</span>
          <span style={{ color: palette.g500, fontWeight: 500, marginLeft: 10, textTransform: 'none', letterSpacing: 0 }}>
            {countLabel} · {formatMoney(group.total)}
          </span>
        </td>
      </tr>
      {group.rows.map((row) => (
        <OutstandingTableRowComponent
          key={row.id}
          row={row}
          onTap={() => onRowTap(row)}
          onRemind={() => onRowRemind(row)}
        />
      ))}
    </>
  );
}
